import {
  analyticsTabs,
  type AnalyticsBudgetStatus,
  type AnalyticsRangePreset,
  type AnalyticsTab,
  type AnalyticsTimeRange,
  type AnalyticsTrend,
} from "@/features/admin/adminAnalytics.types";

const dayMs = 24 * 60 * 60 * 1000;

function toIsoDate(date: Date) {
  return date.toISOString().slice(0, 10);
}

function parseIsoDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

export function isAnalyticsTab(value: string): value is AnalyticsTab {
  return analyticsTabs.includes(value as AnalyticsTab);
}

export function buildAnalyticsRange(
  preset: AnalyticsRangePreset,
  today = new Date(),
  custom?: Pick<AnalyticsTimeRange, "endDate" | "startDate">,
): AnalyticsTimeRange {
  const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  let start = end;

  if (preset === "last_7_days") {
    start = addDays(end, -6);
  } else if (preset === "last_30_days") {
    start = addDays(end, -29);
  } else if (preset === "last_90_days") {
    start = addDays(end, -89);
  } else if (preset === "this_month") {
    start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
  } else if (preset === "last_month") {
    start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() - 1, 1));
    const lastMonthEnd = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 0));
    return {
      bucket: "day",
      endDate: toIsoDate(lastMonthEnd),
      preset,
      startDate: toIsoDate(start),
    };
  } else if (preset === "custom" && custom) {
    return validateAnalyticsRange({
      bucket: "day",
      endDate: custom.endDate,
      preset,
      startDate: custom.startDate,
    });
  }

  return validateAnalyticsRange({
    bucket: "day",
    endDate: toIsoDate(end),
    preset,
    startDate: toIsoDate(start),
  });
}

export function validateAnalyticsRange(range: AnalyticsTimeRange): AnalyticsTimeRange {
  const start = parseIsoDate(range.startDate);
  const end = parseIsoDate(range.endDate);

  if (!start || !end || start > end) {
    throw new Error("ANALYTICS_INVALID_RANGE");
  }

  const days = Math.floor((end.getTime() - start.getTime()) / dayMs) + 1;
  if (days > 366) {
    throw new Error("ANALYTICS_INVALID_RANGE");
  }

  return {
    ...range,
    bucket: days > 90 ? "week" : "day",
  };
}

export function getPreviousEquivalentRange(range: AnalyticsTimeRange): AnalyticsTimeRange {
  const start = parseIsoDate(range.startDate);
  const end = parseIsoDate(range.endDate);
  if (!start || !end) {
    throw new Error("ANALYTICS_INVALID_RANGE");
  }

  const days = Math.floor((end.getTime() - start.getTime()) / dayMs) + 1;
  const previousEnd = addDays(start, -1);
  const previousStart = addDays(previousEnd, -(days - 1));

  return {
    bucket: range.bucket,
    endDate: toIsoDate(previousEnd),
    preset: "custom",
    startDate: toIsoDate(previousStart),
  };
}

export function buildTrend(
  currentValue: number | null | undefined,
  previousValue: number | null | undefined,
  positiveDirection: "up" | "down" = "up",
): AnalyticsTrend {
  if (currentValue == null || previousValue == null || previousValue === 0) {
    return {
      changePercent: null,
      direction: "unavailable",
      previousValue: previousValue ?? null,
      quality: "unavailable",
    };
  }

  const changePercent = ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
  const direction = Math.abs(changePercent) < 0.01 ? "flat" : changePercent > 0 ? "up" : "down";
  const quality = direction === "flat"
    ? "neutral"
    : direction === positiveDirection
      ? "improving"
      : "deteriorating";

  return {
    changePercent,
    direction,
    previousValue,
    quality,
  };
}

export function buildBudgetStatus(spentUSD: number, capUSD: number): AnalyticsBudgetStatus {
  const percent = capUSD > 0 ? (spentUSD / capUSD) * 100 : 0;
  return {
    capUSD,
    percent,
    spentUSD,
    status: percent >= 90 ? "limited" : percent >= 70 ? "warning" : "normal",
  };
}

export function escapeCsvValue(value: unknown) {
  if (value == null) {
    return "";
  }

  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

export function serializeCsv(rows: unknown[][]) {
  return rows.map((row) => row.map(escapeCsvValue).join(",")).join("\r\n");
}
